"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Fire an analytics impression EXACTLY ONCE per logical presentation, no matter
 * how many times the component re-renders.
 *
 * Why this exists (2026-06 offer re-fire incident): the offer card's impression
 * was emitted from a `useEffect` keyed on OBJECT/ARRAY dependencies. When a
 * parent passed unstable references (a non-memoized `visibleChatProducts(result)`
 * array), the effect re-ran on every render and fired `agent_revenue_offer_shown`
 * 5–9× per user — inflating impressions, masking a real click collapse, and
 * flooding Sentry until its quota maxed (100% rejected). Memoizing the parent
 * props fixes the trigger, but the DURABLE fix is to make re-fire impossible by
 * construction: gate emission on a STABLE STRING key via a ref, never on object
 * identity. Every surface that records an impression should route through this
 * hook so the bug class cannot reappear one component at a time.
 *
 * @param key  Stable identity of the presentation, e.g.
 *             ``${planId}:${placement}:${sourcePath}``. Pass null/"" while not
 *             ready; emission waits until a real key arrives, then fires once.
 * @param emit Side effect to run once for that key. It is read from a ref, so a
 *             fresh closure each render is fine — it never re-triggers the effect.
 */
export function useImpressionOnce(key: string | null | undefined, emit: () => void): void {
  const firedKeyRef = useRef<string | null>(null);
  const emitRef = useRef(emit);

  // Keep the latest emit in a ref via an effect (not during render) so a fresh
  // closure each render never re-triggers the fire effect below.
  useEffect(() => {
    emitRef.current = emit;
  });

  useEffect(() => {
    if (!key || firedKeyRef.current === key) return;
    firedKeyRef.current = key;
    emitRef.current();
  }, [key]);
}

/**
 * Returns true once `ready` is true, OR after `fallbackMs` elapses — whichever
 * comes first. Lets an impression WAIT for a decision/data fetch to resolve (so
 * it can log the right details) while GUARANTEEING it still fires if that fetch
 * is slow, cancelled (unstable-deps thrash), or never resolves. Pair with
 * `useImpressionOnce` + a stable constant key for a fire-exactly-once-but-never-
 * zero impression (rule 46/47): the gate that silently dropped ~90% of offer
 * impressions on 2026-06-14 (1,582 -> 156 users) was a readiness flag that
 * never flipped when the /recommend fetch didn't resolve. The timer starts on
 * mount; keep `fallbackMs` constant across renders.
 */
export function useSettled(ready: boolean, fallbackMs = 1200): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setElapsed(true), fallbackMs);
    return () => clearTimeout(timer);
  }, [fallbackMs]);
  return ready || elapsed;
}
